import { LinearProgress } from "@mui/material";

import AuthorizedElement from "@/common/components/Auth/AuthorizedElement";
import AuthorizedTenantElement from "@/common/components/Auth/AuthorizedTenantElement";
import { mapEntityTypeToGeneralScopeType } from "@/common/helpers/entity/entityComments";
import { StringHelper } from "@/common/helpers/string";
import { useApiRequest } from "@/common/hooks/api/useApiRequest";
import { useEntityMeta } from "@/common/hooks/entity/useEntityMeta";
import { enumService } from "@/common/services/enum";
import { BaseEntityDtoAny } from "@/common/ts/entity";
import { apiClient } from "@/core/api/ApiClient";
import { AppPermission, ChatHistoryItemType, ChatType, EntityType } from "@/core/api/generated/v1";

import EmbeddedChat from "./EmbeddedChat";

interface EntityCommentsChatProps {
  entityId: string;
  entityType: EntityType;
  entity: BaseEntityDtoAny | undefined;
}

export default function EntityCommentsChat({
  entityId,
  entityType,
  entity,
}: EntityCommentsChatProps) {
  const entityMeta = useEntityMeta({ entity });

  const entityNumber =
    entityMeta?.localNumber || entityMeta?.externalNumber || entityMeta?.globalNumber;

  const chatRequest = useApiRequest(
    apiClient.chatsApi.apiV1ChatsCommentEntityGetOrCreatePost,
    {
      nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
      getOrCreateCommentChatDto: {
        // users should join explicitly if they want to
        isOnBehalfOfUser: false,
        isAutoJoin: false,
        scope: {
          identifier: entityId,
          type: mapEntityTypeToGeneralScopeType(entityType),
        },
        name: StringHelper.join(
          [
            enumService.getEnumValueName("EntityType", entityType),
            entityMeta && entityNumber ? `(${entityNumber})` : undefined,
          ],
          " ",
        ),
      },
    },
    {
      skip: !entityId,
      deps: [entityId, entityType],
    },
  );
  const chat = chatRequest.data;

  if (chatRequest.isLoading) {
    return <LinearProgress />;
  }

  if (!entity) {
    return null;
  }

  return (
    <AuthorizedElement
      permissions={[AppPermission.FleetAppAccess, AppPermission.ChatRead]}
      withAlert
    >
      <AuthorizedTenantElement tenantIds={[]} withAlert>
        <EmbeddedChat
          chatType={ChatType.Comment}
          chatId={chat?.id}
          chatProps={{
            size: "medium",
            allowPin: true,
            allowClose: false,
            allowLeave: true,
            allowAttachments: true,
            allowOpenInNewTab: false,
            allowEdit: true,
            allowParticipantManagement: true,
            allowAcknowledgement: true,
            allowUpdateTags: true,
            displayProps: {
              actions: true,
            },
            chatHistoryProps: {
              filters: {
                types: [ChatHistoryItemType.ChatMessage],
              },
              displayProps: {
                actions: false,
              },
            },
            chatInputFeatures: {
              tips: false,
            },
          }}
        />
      </AuthorizedTenantElement>
    </AuthorizedElement>
  );
}
